import { useMemo, useState } from "react";
import { Bookmark, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

// =============================================================================
// SavedValuePicker
//
// Tiny "📌 Saved · 3" pill rendered above (or under) form fields. When the
// patient has prior bookings, the API returns deduped lists of values they've
// used — saved pick-up addresses, contact phones, requesters, etc. — and
// this component lets the user click one to fill the form, instead of
// retyping or relying on the auto-prefill (which only fills empty fields).
//
// Designed to be visually unobtrusive: collapsed it's a single line, and it
// disappears entirely when there's nothing to show.
// =============================================================================

interface SavedValuePickerProps<T> {
  /** Short label like "Saved pick-ups". Shows in the trigger pill. */
  label: string;
  /** The list of saved values to choose from — already deduped + sorted by
   *  the API (most recent first). */
  items: T[];
  /** Build the visible label for one entry. First line shows in bold;
   *  optional `meta` line shows underneath in muted text. */
  renderItem: (item: T) => { primary: string; meta?: string };
  /** Called when the user clicks a saved entry. The parent decides how to
   *  apply it (which form fields to set). */
  onSelect: (item: T) => void;
  className?: string;
  /** Optional hint shown under the popover header — e.g. "Click a place to
   *  fill the address fields." */
  description?: string;
}

export function SavedValuePicker<T>({
  label,
  items,
  renderItem,
  onSelect,
  className,
  description,
}: SavedValuePickerProps<T>) {
  const [open, setOpen] = useState(false);
  const count = items.length;

  // Memoise the rendered item descriptors so they don't recompute on every
  // re-render of the parent — this picker often lives inside dirty form
  // sections where every keystroke would otherwise re-run renderItem.
  const rendered = useMemo(
    () => items.map((item) => ({ item, ...renderItem(item) })),
    [items, renderItem],
  );

  if (count === 0) return null;

  return (
    <div className={cn("relative inline-flex", className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-primary/45 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition",
          "hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        <Bookmark className="h-3 w-3" strokeWidth={2.5} />
        <span>
          {label}
          <span className="ml-1 rounded-full bg-primary/30 px-1.5 text-[0.65rem] font-bold text-primary-foreground/90">
            {count}
          </span>
        </span>
        <ChevronDown
          className={cn("h-3 w-3 transition", open && "rotate-180")}
          strokeWidth={2.5}
        />
      </button>

      {open ? (
        <>
          {/* Click-outside backdrop. role=presentation so screen readers
              don't announce it. */}
          <div
            role="presentation"
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            role="listbox"
            className="absolute left-0 top-full z-50 mt-1.5 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-popover shadow-2xl"
          >
            {description ? (
              <p className="border-b border-border px-3 py-2 text-[0.7rem] text-muted-foreground">
                {description}
              </p>
            ) : null}
            <ul className="max-h-72 overflow-y-auto py-1">
              {rendered.map((entry, idx) => (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(entry.item);
                      setOpen(false);
                    }}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition hover:bg-foreground/[0.06]"
                  >
                    <span className="text-sm font-semibold text-foreground">
                      {entry.primary}
                    </span>
                    {entry.meta ? (
                      <span className="text-xs text-muted-foreground">{entry.meta}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}
